import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";

import type { AgeRiskCrosstab } from "@busright/data-contract";
import { RAG } from "@busright/ui";

import { formatInt, formatPct } from "@/lib/format";

// REAL age-cohort (rows) × risk-tier (cols) crosstab. The /api/lifecycle payload now carries a
// genuine roster⋈latest-risk join, so these are MEASURED device counts — not a modeled stub. The
// at-risk column is color-encoded by the cohort's at-risk share (WARN+CRITICAL); because that is a
// real device-HEALTH signal, a RAG-warm tint is in-bounds here (unlike the old modeled ramp).

const RISK_COLS = [
  { key: "ok", label: "OK", color: RAG.OK },
  { key: "warn", label: "Warn", color: RAG.WARN },
  { key: "critical", label: "Critical", color: RAG.CRITICAL },
] as const;

// Map an at-risk share (0..100) onto an alpha so denser at-risk cohorts read warmer. Anchored at a
// ~15% ceiling so the real spread (5.7%–11.8%) is legible rather than washed out.
const AT_RISK_CEILING = 15;
const MIN_ALPHA = 0.12;
const MAX_ALPHA = 0.85;
const PCT_SCALE = 100;

const atRiskFill = (pct: number): string => {
  const t = Math.min(1, pct / AT_RISK_CEILING);
  const alpha = MIN_ALPHA + (MAX_ALPHA - MIN_ALPHA) * t;
  return `color-mix(in srgb, ${RAG.WARN} ${Math.round(alpha * PCT_SCALE)}%, transparent)`;
};

export interface LcAgeRiskMatrixProps {
  readonly crosstab: AgeRiskCrosstab;
}

export const LcAgeRiskMatrix = ({ crosstab }: LcAgeRiskMatrixProps): ReactNode => {
  const navigate = useNavigate();

  return (
    <div>
      <div style={{ overflowX: "auto" }}>
        <table className="data-table" style={{ minWidth: 460 }}>
          <thead>
            <tr>
              <th>Cohort</th>
              <th className="num">Devices</th>
              {RISK_COLS.map((c) => (
                <th key={c.key} className="num">
                  {c.label}
                </th>
              ))}
              <th className="num">At-risk</th>
            </tr>
          </thead>
          <tbody>
            {crosstab.cohorts.map((row) => (
              <tr
                key={row.cohort}
                onClick={() => void navigate("/health")}
                style={{ cursor: "pointer" }}
                title={`${row.label}: ${formatPct(row.atRiskPct)} at-risk — open Fleet Health`}
              >
                <td>{row.label}</td>
                <td className="num br-tabular-nums">{formatInt(row.total)}</td>
                {RISK_COLS.map((c) => (
                  <td key={c.key} className="num br-tabular-nums" style={{ color: c.color }}>
                    {formatInt(row.riskCounts[c.key])}
                  </td>
                ))}
                <td
                  className="num br-tabular-nums"
                  style={{
                    background: atRiskFill(row.atRiskPct),
                    color: "var(--br-text-primary)",
                    fontWeight: 600,
                  }}
                >
                  {formatPct(row.atRiskPct)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div
        style={{
          display: "flex",
          gap: 8,
          alignItems: "center",
          marginTop: 12,
          fontSize: 11,
          color: "var(--br-text-muted)",
        }}
      >
        <span>0%</span>
        <span
          aria-hidden
          style={{
            flex: 1,
            height: 8,
            borderRadius: 4,
            background: `linear-gradient(90deg, ${atRiskFill(0)}, ${atRiskFill(AT_RISK_CEILING)})`,
          }}
        />
        <span>{AT_RISK_CEILING}%+</span>
        <span style={{ marginLeft: 8 }}>at-risk share (WARN + CRITICAL)</span>
      </div>

      <p style={{ marginTop: 10, fontSize: 11, color: "var(--br-text-muted)" }}>
        Real device counts — provisioning-age cohort joined to each device's latest risk tier across{" "}
        {formatInt(crosstab.scoredDevices)} scored devices. Rows drill into Fleet Health.
      </p>
    </div>
  );
};
